import { type FC, useState } from "react"
import styled, { css } from "styled-components"

import { parseIntNumber } from "@forento/shared/utilities/number"
import { exhaustiveGuard } from "@forento/shared/utilities/switch"

import { type Course, useCourses } from "~/api/course"
import { usePlatform } from "~/contexts/PlatformContext"
import useQueryParam from "~/hooks/useQueryParam"
import Button from "~/themes/school/components/Button"
import Card, { CardText, CardThumbnail, CardThumbnailSticker, CardTitle, Cards } from "~/themes/school/components/Card"
import { TitleSearch } from "~/themes/school/components/ContentSearch"
import { CheckIcon } from "~/themes/school/components/Icon"
import Layout from "~/themes/school/components/Layout"
import PageHeader, { PageTitle } from "~/themes/school/components/PageHeader"
import PartialLoadingPage from "~/themes/school/components/PartialLoadingPage"
import { fonts, lightTextColor, successColor } from "~/themes/school/styles"
import { useTranslation } from "~/translations"
import routes from "~/utilities/routes"

const CoursesPage: FC = () => {
	const courses = useCourses()
	const t = useTranslation()
	const selectedTag = useQueryParam("tag")

	const [searchItems, setSearchItems] = useState<number[]>()

	return (
		<Layout>
			<PageHeader>
				<PageTitle>{t("course.title")}</PageTitle>
				<TitleSearch
					items={courses.status === "success" ? courses.data : []}
					onItemsToShowChange={setSearchItems}
				/>
			</PageHeader>
			{courses.status === "error" ? (
				<p>{t("failedToLoad")}</p>
			) : courses.status === "loading" ? (
				<PartialLoadingPage />
			) : courses.status === "success" ? (
				<>
					{courses.tags.length > 0 && (
						<Tags>
							{courses.tags.map(tag => (
								<Tag
									key={tag.id}
									$isActive={selectedTag.value === tag.id.toString()}
									onClick={() => selectedTag.set(tag.id.toString())}
								>
									{tag.label}
								</Tag>
							))}
							{selectedTag.value !== null && (
								<Tag $isActive onClick={() => selectedTag.set(null)}>
									{t("clearFilter")}
								</Tag>
							)}
						</Tags>
					)}
					<Courses
						courses={courses.data}
						selectedTag={parseIntNumber(selectedTag.value)}
						searchItems={searchItems}
					/>
				</>
			) : (
				exhaustiveGuard(courses.status)
			)}
		</Layout>
	)
}

type CoursesProps = { courses: Course[]; selectedTag: number | null; searchItems: number[] | undefined }
const Courses: FC<CoursesProps> = ({ courses, selectedTag, searchItems }) => {
	const platform = usePlatform().platform

	let filteredCourses = courses
	if (selectedTag !== null) {
		filteredCourses = filteredCourses.filter(course => course.tagIds.includes(selectedTag))
	}
	if (searchItems !== undefined) {
		filteredCourses = filteredCourses
			.filter(course => searchItems.includes(course.id))
			.sort((a, b) => (searchItems ? searchItems.indexOf(b.id) - searchItems.indexOf(a.id) : 0))
	}
	return (
		<Cards>
			{filteredCourses.map(course => (
				<Card key={course.id} as={Button} onClick={routes.course.detail(course.id)}>
					{platform.id !== CONFIG.bokaderoPlatformId && (
						<CourseTitle $isCompleted={course.progress === 1}>
							{course.title}
							{course.progress === 1 && <CourseTitleCheckIcon />}
						</CourseTitle>
					)}
					<CardThumbnail
						filePath={course.thumbnailFilePath}
						sticker={
							<>
								<CardThumbnailSticker>{course.priceLabel.value}</CardThumbnailSticker>
								<CardThumbnailPages>{course.pagesLabel}</CardThumbnailPages>
							</>
						}
					/>
					{platform.id === CONFIG.bokaderoPlatformId && (
						<CourseTitle $isCompleted={course.progress === 1}>
							{course.title}
							{course.progress === 1 && <CourseTitleCheckIcon />}
						</CourseTitle>
					)}
					<CardText>{course.shortDescription}</CardText>
				</Card>
			))}
		</Cards>
	)
}

const Tags = styled.div`
	width: 100%;
	display: flex;
	gap: 22px;
	flex-wrap: wrap;
	margin-bottom: 24px;
`

const Tag = styled(Button)<{ $isActive: boolean }>`
	font-family: ${fonts.quicksand};
	font-size: 14px;
	font-weight: bold;
	color: ${props => (props.$isActive ? props.theme.primaryColor : lightTextColor)};
`

const CourseTitle = styled(CardTitle)<{ $isCompleted: boolean }>`
	${props =>
		props.$isCompleted &&
		css`
			color: ${successColor};
			display: flex;
			align-items: center;
			gap: 8px;
		`}
`

const CourseTitleCheckIcon = styled(CheckIcon)`
	width: 12px;
	height: 12px;
`

const CardThumbnailPages = styled.div`
	position: absolute;
	bottom: 16px;
	right: 16px;
	font-weight: normal;
	font-size: 12px;
	color: ${lightTextColor};
	background-color: rgba(255, 255, 255, 0.7);
	padding: 4px 8px;
	border-radius: 4px;
`

export default CoursesPage
